import * as React from 'react';
import Button from '@mui/material/Button';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';


const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

export interface DialogTitleProps {
  id: string;
  children?: React.ReactNode;
  onClose: () => void;
}

function BootstrapDialogTitle(props: DialogTitleProps) {
  const { children, onClose, ...other } = props;

  return (
    <DialogTitle sx={{ m: 0, p: 2 }} {...other}>
      {children}
      {onClose ? (
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </DialogTitle>
  );
}

export default function AiAgentsModal({message} :any) {

  const [open, setOpen] = React.useState(false);

  const handleClickOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <span>
      <a href="" onClick={handleClickOpen}>{message}</a>
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <BootstrapDialogTitle id="customized-dialog-title" onClose={handleClose}>
          AI Analysts/Assistants
        </BootstrapDialogTitle>
        <DialogContent dividers>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{modalData}</ReactMarkdown>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </BootstrapDialog>
    </span>
  );
}

const modalData = `
# AI Assistants
Our AI Assistants are co-pilots for Stock Market data research and analysis. We offer three AI Assistants:
* Lima
* Summer
* June
* Saturday

## Lima
[Lima](/chat) is our most versatile AI Stock Market Research Analyst co-pilot.

#### Lima has access to the following data:
* Company stock quote (price, volume, market cap, etc.)
* Company profile
* Key company product information
* Key company financial ratios & metrics
* Latest earnings call transcript of the company
* Recent news headlines and summaries about the company
* Company press releases
* 15+ years of the company's financial statements, income, balance sheets, and cash flow statements
* Company executives compensation
* Company insider trading activity
* Access to all the company's past earnings call transcripts
* Key Ratios for the Trailing Twelve Months (TTM)
* Analyst Earnings Estimates
* Historical Analyst Recommendations
* Historical Dividends
* Stock Splits
* Historical key Ratios, Owner Earnings, and Enterprise Value
* Price Target Summary
* Analysts Price Targets
* Historical stock price and Intraday price chart
* ESG ratings and benchmark
* Daily most active, gainer and loser stocks
and much more.

#### Web Browsing and Web Search Capabilities
Lima has advanced web integration features that significantly expand its knowledge and capabilities:
- **Real-time Web Search**: Lima can perform live internet searches to access up-to-date information beyond our dataset.
- **Website Navigation**: Lima is capable of browsing and analyzing content from specific websites to gather targeted information.
- **Information Synthesis**: By combining data from our extensive internal dataset with newly acquired web information, Lima provides comprehensive and current answers.
- **Enhanced User Value**: This dual-source approach allows Lima to offer more detailed, accurate, and timely responses, maximizing the value of each interaction.

These features enable Lima to assist with a wide range of tasks, from answering complex queries to providing the latest news and developments in various fields.

### Stock Evaluation Strategies and Criteria
Lima uses a long-term value-growth investing strategy to evaluate stocks. Similar to investing strategies used by investors like Phil Town, Warren Buffet, Monish Pabrai, and Charlie Munger.

### Usage Costs
Usage costs for Lima:
* Free: 4 Questions/Tasks (doesn't expire)
* Premium plan members: 40 Questions/Tasks every month. Unused credits will accumulate until used.
* Executive plan members: **Unlimited** Questions/Tasks

## Summer
[Summer](/ai_stock_analysis) is our most sophisticated AI Assistant. Summer is an AI Stock Market Research Analyst co-pilot. Summer only covers US public companies (We plan to expand this soon).
#### Summer has access to the following data:
* Company stock quote (price, volume, market cap, etc.)
* Company profile
* Key company product information
* Key company financial ratios & metrics
* Latest earnings call transcript of the company
* Recent news headlines and summaries about the company
* Company press releases
* 15+ years of the company's key financial records
* historical dividends
* Key Ratios for the Trailing Twelve Months (TTM)
* Analyst Earnings Estimates
* Company executives compensation
* Company insider trading activity

### Stock Evaluation Strategies and Criteria
Summer uses the same strategies as Lima to evaluate stocks.

#### Summer looks out for the following, among other things:
* Consistent high ROE and ROIC over ten years or more. 10% or higher. The higher, the better.
* Consistent growth rate (CAGR) of revenue, earnings, equity, and cash flow over 1,3,5,7,10 years. 10% or higher each year. The higher the growth rate, the better. A growth rate that's trending higher is preferred.
* Manageable debt levels.
* Good liquidity ratios.
* Strong brand moat.
* CEO and management team with a good track record.
* Long tenure of the management team.
* Does the management team align with shareholders' interests? (checks on management team compensation, stock dilution)
* Is the stock fairly valued?
* Formulas for the company's intrinsic value calculation using the provided data.
* Headwinds, tailwinds, opportunities, and risks from Earnings call transcripts, recent news, and press releases.

### Usage Costs
Usage costs for Summer:
* Free: 4 Questions/Tasks (doesn't expire)
* Premium plan members: 40 Questions/Tasks (per month)
* Executive plan members: **Unlimited** Questions/Tasks (per month)
* Enterprise plan members: Customized version of our most advanced AI Assistant, Summer, configured to use your strategies and criteria to analyze and filter stock. (Contact us for more details at support@decodeinvesting.com)

## Differences between AI assistants Summer and Lima

While both AI Assistants are now very similar in capabilities, there are a few differences:
* **Lima:** More efficient, can analyze more companies and data simultaneously. Best for most day-to-day tasks.
* **Summer:** Slightly wiser, uses more data per request. Ideal for open-ended questions about investments.

For example, ask **Summer:** "Is the company a good potential investment?" Summer excels at providing a comprehensive overview.

Ask **Lima:** "Analyze the balance sheet of Uber vs. Lyft." Lima handles detailed, specific questions exceptionally well.

## June
[June](/sec_filings) specializes in analyzing and reviewing SEC filing documents like 10-K, 20-F, 10-Q, and 8-K filings. June can understand an SEC filing document in its entirety and can analyze, review, and provide insights and summaries on it.

### Favorite Prompts/Tasks for 10-K Report Analysis
Our favorite prompts for June for 10-K Report Analysis are:
\`\`\`
perform a swot analysis on this 10-K report
\`\`\`
and
\`\`\`
describe the good, bad and ugly about the company, based on the information provided on this 10-k report,
create a section for good, bad, and ugly. put contents of each section into a list. Make all headings bold.
\`\`\`
and
\`\`\`
Summarize the key financial and product highlights from the 10-K filing?
\`\`\`
June has read the entire SEC Filing and can give you good questions to ask about the report. To get some good ideas, ask June:
\`\`\`
what are some good questions to ask you?
\`\`\`

### Usage Costs
Usage costs for Saturday:
* Free: 4 Questions/Tasks (doesn't expire)
* Premium plan members: 40 Questions/Tasks (unused credits roll over to the next month until used)
* Executive plan members: **Unlimited** Questions/Tasks (per month)

## Saturday
[Saturday](/earnings_calls) is a specialized AI Stock Market Research Assistant for reviewing and analyzing earnings call transcripts. Saturday has access to the following data:
* Earnings Call transcript specified by the user.

### Favorite Prompts/Tasks
Our favorite prompts for Saturday are:
\`\`\`
Describe the good, bad and ugly about this earnings call,
create a section for good, bad, and ugly.
put contents of each section into a list
\`\`\`
and
\`\`\`
Can you put the financial and product metrics reported in the call into a list?
Add a section for financial metrics and another section for product metrics
\`\`\`

Saturday can give you ideas of questions/tasks to ask for the specific earnings call. To get some good ideas, ask Saturday:
\`\`\`
Give me a list of 20 questions or tasks I should ask you.
\`\`\`

*Fun Fact:* The contents of this subreddit [/r/EarningsCalls](https://www.reddit.com/r/EarningsCalls/) is generated by Saturday.

### Usage Costs
Usage costs for Saturday:
* Free: 4 Questions/Tasks (doesn't expire)
* Premium plan members: 40 Questions/Tasks (unused credits roll over to the next month until used)
* Executive plan members: **Unlimited** Questions/Tasks (per month)

## Limitations of the AI Assistants
The AI assistants are only tools that help with stock market research, but they are not a replacement for human judgment. The AI assistants can generate insights and recommendations, but it is important to remember that these are just suggestions. You should always do your own research or consult with a financial advisor before making any investment decisions.

The stock market is complex and volatile, and there is no guarantee that any investment will be profitable.

## Reliability and Safety
The AI Assistants are still under development, and we are constantly working to improve them. However, it is important to note that no AI system is perfect, and there is always the possibility of errors. We use a special access person/human reviewer to manually review all the AI responses for reliability and safety after the response has been published. If you see any errors or have any feedback, please let us know at support@decodeinvesting.com.

We hope the AI Assistants will be a valuable tool for you, but we urge you to use them responsibly.
`
